import { MouseEvent, ElementType } from 'react';
import styles from './Button.module.scss';
import { Tooltip } from '@components';
import {
	BUTTON_ACTION_TYPE, BUTTON_ICON_POS, BUTTON_SIZE, BUTTON_TYPES, TOOLTIP_POSITION
} from '@types';

export type ButtonProps = {
	id?: string;
	'data-testid'?: string;
	text: string | JSX.Element;
	onClick: (e:MouseEvent<HTMLButtonElement>) => any;
	type?: BUTTON_TYPES;
	size?: BUTTON_SIZE;
	disabled?: boolean;
	Icon?: ElementType;
	iconPos?: BUTTON_ICON_POS;
	loading?: boolean;
	/** @deprecated not used */
	loaderSize?: number;
	/** @deprecated not used */
	loaderThickness?: number;
	/** @deprecated not used */
	loaderColor?: string;
	/** @deprecated not used */
	loaderTrayColor?: string;
	actionType?: BUTTON_ACTION_TYPE;
	stretch?: boolean;
	inActive?: boolean;
	iconDisableColor?: string;
	iconActiveColor?: string;
	iconOnly?: boolean;
	iconSmall?: boolean;
	inverse?: boolean;
	tooltipPosition?: TOOLTIP_POSITION;
}

export const Button = (props:ButtonProps) => {
	const {
		id,
		'data-testid': dataTestId,
		text,
		onClick,
		type = BUTTON_TYPES.PRIMARY,
		size = BUTTON_SIZE.LARGE,
		disabled = false,
		Icon = null,
		iconPos = BUTTON_ICON_POS.LEFT,
		loading = false,
		iconActiveColor= '',
		iconDisableColor = '',
		actionType = BUTTON_ACTION_TYPE.BUTTON,
		stretch = false,
		inActive = false,
		iconOnly = false,
		iconSmall = false,
		inverse = false,
		tooltipPosition = TOOLTIP_POSITION.TOP
	} = props;
	const disabledOrLoading = disabled || loading;
	const addBtnType = () => {
		switch (type) {
		case BUTTON_TYPES.SECONDARY:
			return styles.secondary;
		case BUTTON_TYPES.TERTIARY:
			return styles.tertiary;
		case BUTTON_TYPES.ACTION:
			return styles.action;
		case BUTTON_TYPES.POSITIVE:
			return styles.positive;
		case BUTTON_TYPES.NEGATIVE:
			return styles.negative;
		default:
			return styles.primary;
		}
	}
	const addBtnDisabled = () => {
		switch (type) {
		case BUTTON_TYPES.PRIMARY:
			return styles.primaryDisabled;
		case BUTTON_TYPES.SECONDARY:
			return styles.disabled;
		case BUTTON_TYPES.TERTIARY:
			return styles.tertiaryDisabled;
		case BUTTON_TYPES.ACTION:
			return styles.actionDisabled;
		case BUTTON_TYPES.POSITIVE:
			return styles.positiveDisabled;
		case BUTTON_TYPES.NEGATIVE:
			return styles.negativeDisabled;
		default:
			return styles.disabled;
		}
	}
	const addBtnSize = () => {
		switch (size) {
		case BUTTON_SIZE.SMALL:
			return styles.small;
		default:
			return styles.large;
		}
	}
	const triggerOnClick = (e:MouseEvent<HTMLButtonElement>) => {
		e.currentTarget?.blur();
		if (!disabledOrLoading) onClick(e);
	}

	const iconStyle = disabledOrLoading && iconDisableColor
		? { color: iconDisableColor, fill: iconDisableColor }
		: iconActiveColor
			? { color: iconActiveColor, fill: iconActiveColor }
			: {};

	return (
		<button
			id={id}
			data-testid={dataTestId}
			type={actionType}
			onClick={triggerOnClick}
			disabled={disabledOrLoading}
			className={`${styles.container} ${addBtnType()} ${addBtnSize()} ${disabledOrLoading ? addBtnDisabled() : ''} ${stretch ? styles.stretch : ''} ${inActive ? styles.inActive : ''} ${iconOnly && styles.iconButton} ${loading ? styles.loading : ''} ${inverse ? styles.inverse : ''}`}
			aria-label={`${typeof text === 'string' ? text : ''} button`}
		>
			{
				iconOnly && Icon !== null &&
				<Tooltip text={text} position={tooltipPosition}>
					<Icon
						data-testid={'@Ofload-Btn-Icon-Only'}
						className={`${styles.iconOnly} ${iconSmall ? styles.iconSmall : ''}`}
						style={iconStyle}
					/>
				</Tooltip>
			}
			{!iconOnly && <span className={`${styles.inner} ${type === BUTTON_TYPES.ACTION && styles.innerAction}`}>
				{iconPos === BUTTON_ICON_POS.LEFT && Icon !== null && <Icon data-testid={'@Ofload-Btn-Icon-Left'} className={styles.iconLeft} style={iconStyle}/>}
				{iconPos === BUTTON_ICON_POS.TOP && Icon !== null && <Icon data-testid={'@Ofload-Btn-Icon-Top'} className={styles.iconTop} style={iconStyle}/>}
				{text}
				{iconPos === BUTTON_ICON_POS.RIGHT && Icon !== null && <Icon data-testid={'@Ofload-Btn-Icon-Right'} className={`${styles.iconRight} ${iconSmall ? styles.iconSmall : ''}`} style={iconStyle}/>}
				{iconPos === BUTTON_ICON_POS.BOTTOM && Icon !== null && <Icon data-testid={'@Ofload-Btn-Icon-Bottom'} className={styles.iconBottom} style={iconStyle}/>}
			</span>}
		</button>
	);
}

export const ActionButton = (props: ButtonProps) => {
	const {
		text,
		onClick
	} = props;
	return <Button {...props} type={BUTTON_TYPES.ACTION} text={text} onClick={onClick} iconPos={BUTTON_ICON_POS.TOP} />
}
